import React, { useState } from 'react';

// Modal para seleccionar opciones/modificadores de un producto
// (ej: término de cocción, guarnición, con/sin papas, tamaño, etc.)
const ModifierModal = ({ product, onClose, onAdd }) => {
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [itemNotes, setItemNotes] = useState('');

  if (!product) return null;

  const handleToggleOption = (modifierId, option) => {
    const modifier = product.modifiers?.find(m => m.id === modifierId);
    if (!modifier) return;

    setSelectedModifiers(prev => {
      const current = prev[modifierId] || [];
      const exists = current.find(c => c.id === option.id);
      const max = modifier.max_selections || 1;

      if (exists) {
        // Quitar si ya está seleccionado
        return { ...prev, [modifierId]: current.filter(c => c.id !== option.id) };
      }

      if (max === 1) {
        // Selección única: reemplaza directamente (ideal para "término medio/cocido", "con/sin papas")
        return { ...prev, [modifierId]: [option] };
      }

      if (current.length >= max) {
        // Reemplazar el primero si excede el máximo permitido
        return { ...prev, [modifierId]: [...current.slice(1), option] };
      }

      return { ...prev, [modifierId]: [...current, option] };
    });
  };

  const isOptionSelected = (modifierId, optionId) => {
    return selectedModifiers[modifierId]?.some(o => o.id === optionId) || false;
  };

  const isMissingRequired = () => {
    return (product.modifiers || []).some(mod => {
      const min = mod.min_selections ?? (mod.is_required ? 1 : 0);
      const count = (selectedModifiers[mod.id] || []).length;
      return min > 0 && count < min;
    });
  };

  const handleAdd = () => {
    if (isMissingRequired()) {
      alert('Falta seleccionar alguna opción obligatoria');
      return;
    }

    // Convertir selectedModifiers a array plano
    const modifiersArray = [];
    Object.values(selectedModifiers).forEach(opts => {
      opts.forEach(opt => {
        modifiersArray.push({
          modifier_option_id: opt.id,
          option_name: opt.name,
          price_adjustment: opt.price_adjustment || 0,
          quantity: 1,
        });
      });
    });

    onAdd(product, modifiersArray, itemNotes);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', zIndex: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} onClick={onClose}>
      <div className="card animate-fade-in" style={{
        maxWidth: 480, width: '100%', maxHeight: '80vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#e2e8f0' }}>⚙️ Personalizar: {product.name}</h3>
          <button onClick={onClose} style={{
            background: '#334155', color: '#e2e8f0', border: 'none',
            borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
          }}>✕</button>
        </div>

        <p style={{ color: '#fbbf24', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>
          ${product.price?.toFixed(2)}
        </p>

        {/* Modificadores */}
        {product.modifiers?.map(modifier => (
          <div key={modifier.id} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h4 style={{ color: '#94a3b8', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1 }}>
                {modifier.name}
                {(modifier.is_required || modifier.min_selections > 0) && <span style={{ color: '#ef4444' }}> *</span>}
              </h4>
              <span style={{ color: '#64748b', fontSize: 11 }}>
                {modifier.min_selections > 0 ? `Mín ${modifier.min_selections}` : ''}
                {modifier.max_selections > 1 ? ` · Máx ${modifier.max_selections}` : ''}
              </span>
            </div>
            {modifier.description && (
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 8 }}>{modifier.description}</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {modifier.options?.map(option => (
                <button
                  key={option.id}
                  onClick={() => handleToggleOption(modifier.id, option)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', borderRadius: 8, border: '1px solid',
                    borderColor: isOptionSelected(modifier.id, option.id) ? '#38bdf8' : '#334155',
                    background: isOptionSelected(modifier.id, option.id) ? 'rgba(56, 189, 248, 0.1)' : '#0f172a',
                    color: isOptionSelected(modifier.id, option.id) ? '#38bdf8' : '#e2e8f0',
                    cursor: 'pointer', fontSize: 14,
                  }}
                >
                  <span>{option.name}</span>
                  <span style={{ color: option.price_adjustment > 0 ? '#fbbf24' : '#64748b', fontWeight: 600 }}>
                    {option.price_adjustment > 0 ? `+$${option.price_adjustment.toFixed(2)}` : option.price_adjustment < 0 ? `-$${Math.abs(option.price_adjustment).toFixed(2)}` : 'Incluido'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Notas */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>Notas especiales</label>
          <input
            type="text"
            className="input"
            placeholder="Ej: sin cebolla, bien cocido, etc."
            value={itemNotes}
            onChange={e => setItemNotes(e.target.value)}
          />
        </div>

        <button onClick={handleAdd} className="btn btn-success" style={{ width: '100%', padding: 14 }}>
          ➕ Agregar al Pedido
        </button>
      </div>
    </div>
  );
};

export default ModifierModal;